import { CTAQueryStateType } from '../declaration';
import { BrandFeatureName, FeatureName } from '../features';

export const B2CRouteName = {
  Unauthorized: 'Unauthorized',
  CTA: 'CTA',
  PreDeclaration: 'PreDeclaration',
  OpenArrival: 'OpenArrival',
  VehicleTypeChoice: 'VehicleTypeChoice',
  CustomerInformations: 'CustomerInformations',
  CustomerInformationsForm: 'CustomerInformationsForm',
  GlassChoice: 'GlassChoice',
  ImpactType: 'ImpactType',
  ImpactSize: 'ImpactSize',
  FieldView: 'FieldView',
  Mirror: 'Mirror',
  LicensePlate: 'LicensePlate',
  SinisterInfo: 'SinisterInfo',
  Address: 'Address',
  Adas: 'Adas',
  InsuranceInformation: 'InsuranceInformation',
  AppointmentSearch: 'AppointmentSearch',
  FitterPage: 'FitterPage',
  FitterComments: 'FitterComments',
  FitterOpeningHours: 'FitterOpeningHours',
  AppointmentValidation: 'AppointmentValidation',
  Summary: 'Summary',
  ModifyAppointment: 'ModifyAppointment',
  ModifyAppointmentConfirm: 'ModifyAppointmentConfirm',
  CancelAppointment: 'CancelAppointment',
  CancelAppointmentConfirm: 'CancelAppointmentConfirm',
  AppointmentSuggestion: 'AppointmentSuggestion',
  UploadDocuments: 'UploadDocuments',
  UploadDocumentsEnd: 'UploadDocumentsEnd',
  CallBackForm: 'CallBackForm',
} as const;
export type B2CRouteName = typeof B2CRouteName[keyof typeof B2CRouteName];

export const B2CScreen = {
  Desktop: 'Desktop',
  Landscape: 'Landscape',
  Mobile: 'Mobile',
} as const;
export type B2CScreen = typeof B2CScreen[keyof typeof B2CScreen];

export const B2CAccessConditionNameEnum = {
  NoPrevious: 'no-previous',
  NoNext: 'no-next',
  NoLastAccessible: 'no-last-accessible',
  OnlyStoredNext: 'only-stored-next',
  HasStoredSelectedFitter: 'has-stored-selected-fitter',
  UserCanBookAppointment: 'user-can-book-appointment',
  DeclIsGlassInfosFilled: 'decl-is-glass-infos-filled',
  DeclIsSinisterDateSet: 'decl-is-sinister-date-set',
  DeclIsCarSet: 'decl-is-car-set',
  DeclIsLicensePlateSet: 'decl-is-license-plate-set',
  DeclIsGlassSet: 'decl-is-glass-set',
  CarHasUnknownAdas: 'car-has-unknown-adas',
  DeclIsAdasSet: 'decl-is-adas-set',
  DeclIsAdas: 'decl-is-adas',
  HasSearchCriteriaAddress: 'has-search-criteria-address',
  ModificationCutoffAllowed: 'mofification-cutoff-allowed',
  CancelCutoffAllowed: 'cancel-cutoff-allowed',
  NotModified: 'not-modified',
  NotCancelled: 'not-cancelled',
  DossierIdExists: 'dossier-id-exists',
  AppointmentIdExists: 'appointment-id-exists',
  DeclIsWindshieldGlass: 'decl-windshield-glass',
  DeclIsReparaibleWindshieldImpactType:
    'decl-is-repairable-windshield-glass-impact-type',
  DeclIsReparaibleWindshieldImpactSize:
    'decl-is-repairable-windshield-glass-impact-size',
  DeclIsRepairOperation: 'decl-is-repair-operation',
  DeclIsReplaceOperation: 'decl-is-replace-operation',
  DeclIsMirrorGlass: 'decl-is-mirror-glass',
  HasNoStatusAppointment: 'has-no-status-appointment',
  NotBookedOrRequested: 'not-requested-or-booked',
  DossierIsComplete: 'dossier-is-complete',
  HasRequestedAppointment: 'is-requested-appointment',
  CallbackCarType: 'callback-car-type',
  CallbackGlassChoice: 'callback-glass-choice',
  DeclarationEndGlassChoice: 'declaration-end-glass-choice',
  isDossierCallback: 'is-dossier-callback',
  IsDossierSlotsSuggested: 'is-dossier-slots-suggested',
  IsDossierCovered: 'is-dossier-covered',
  IsDossierCreatedByInsurer: 'is-dossier-created-by-insurer',
  IsMobile: 'is-mobile',
  IsLandscape: 'is-landscape',
  IsDesktop: 'is-desktop',
  FromSpecificPage: 'from-specific-page',
  HasWeatherEvents: 'has-weather-events',
  IsAgendaOrigin: 'is-agenda-origin',
  CanEditDossierAfterApiCreation: 'can-edit-dossier-after-api-creation',
} as const;
export type B2CAccessConditionNameEnum =
  typeof B2CAccessConditionNameEnum[keyof typeof B2CAccessConditionNameEnum];
export type B2CAccessConditionFFName = `FF/${FeatureName}`;
export type B2CAccessConditionFFBName = `FF/B/${BrandFeatureName}`;
export type B2CAccessConditionName =
  | B2CAccessConditionNameEnum
  | B2CAccessConditionFFName
  | B2CAccessConditionFFBName;

export const B2CActionOnFailEnum = {
  ModificationCutoffSnackbar: 'modification-cutoff-snackbar',
  CancelCutoffSnackbar: 'cancel-cutoff-snackbar',
} as const;
export type B2CActionOnFailEnum =
  typeof B2CActionOnFailEnum[keyof typeof B2CActionOnFailEnum];

export interface B2CRouting {
  pages: B2CRoutingPage[];
}

export interface B2CRoutingPage {
  name: B2CRouteName;
  urlList: string[];
  screenDependencies: ScreenDependencies;
  accessConditions?: AccessCondition[];
  specialRedirections?: SpecialRedirection[];
  declarationStep: DeclarationStep;
  navbarBehavior?: RoutingNavbarBehavior;
  additionalOptions?: AdditionalPageOptions;
  specialActions?: SpecialActions[];
}

export interface AccessCondition {
  name: B2CAccessConditionName;
  successValue: boolean;
  redirectOnFail?: string; // Corresponds to SpecialRedirection.name existing in the same page
  actionOnFail?: string;
  subConditionsOnSuccess?: AccessCondition[];
  subConditionsOnFail?: AccessCondition[];
  options?: Record<string, any>;
}

export interface DeclarationStep {
  pageStep: number;
  isDefault?: boolean;
  isCompleted?: boolean;
  disableLinks?: boolean;
  isStepDisabled?: boolean;
}

export interface RoutingNavbarBehavior {
  displaySummary?: 'open' | 'close' | 'none'; // undefined has same behavior as 'none'
  hideGoBack?: boolean;
}

export interface ScreenDependencies {
  allowedScreens: B2CScreen[];
  onResizeRedirect: B2CRouteName;
}

export const SpecialRedirectionCommand = {
  Previous: '#previous',
  Next: '#next',
  LastAccessible: '#last-accessible',
  HistoryBack: '#history-back',
} as const;
export type SpecialRedirectionCommand =
  typeof SpecialRedirectionCommand[keyof typeof SpecialRedirectionCommand];

export interface SpecialRedirection {
  name: string;
  pageName: B2CRouteName | SpecialRedirectionCommand;
  defaultRedirect: boolean;
  options?: {
    queryParams?: {
      [key: string]: string;
    };
    navigationExtras?: string[];
  };
}

export interface AdditionalPageOptions {
  skippableOnLastAccessible?: boolean;
  skippableStepperInfo?: boolean;
  triggerScriptIds?: string[];
  displayWeatherEvent?: boolean;
}

export interface RoutingContext {
  isCommandNext?: boolean;
  isCommandPrevious?: boolean;
  isCommandLastAccessible?: boolean;
  isCommandHistoryBack?: boolean;
  stored?: CTAQueryStateType;
  accessConditionOptions?: AccessCondition['options'];
}

export interface SpecialActions {
  name: SpecialActionName;
  conditions: Omit<
    AccessCondition,
    'redirectOnFail' | 'actionOnFail' | 'options'
  >[];
}

export const SpecialActionName = {
  OperationDialog: 'operation-dialog',
  AdasDialog: 'adas-dialog',
  WeatherInfoDialog: 'weather-info-dialog',
} as const;
export type SpecialActionName =
  typeof SpecialActionName[keyof typeof SpecialActionName];

export const B2BRoutingName = {
  CustomerDataForm: 'CustomerDataForm',
  SinisterData: 'SinisterData',
  AppointmentSearch: 'AppointmentSearch',
  FitterSearch: 'FitterSearch',
  FitterOpennings: 'FitterOpennings',
  DeclarationEnd: 'DeclarationEnd',
} as const;
export type B2BRouteName = typeof B2BRoutingName[keyof typeof B2BRoutingName];

export type RoutingName = B2BRouteName | B2CRouteName;
